// P-10 resilience (Frontend §4.10 item 3, §3.5 rows for #10; API.md §1.11): the client side of the booking replay.
import type { ApiErrorCode } from '@pa/api'
import type { Outcome } from '../model/machine'
import { BOOKING_TIMEOUT_MS } from './usePatientApi'

/** `409 request_in_progress`: wait this long, then send again with the same key (Frontend §4.10 (a)). */
export const IN_PROGRESS_WAIT_MS = 1000

export interface ConfirmOptions {
  sleep?: (ms: number) => Promise<void>
  now?: () => number
  /** The confirm attempt's whole client budget: the booking timeout (Frontend §4.8, §4.10 (b)). */
  budgetMs?: number
}

const realSleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

const LOST: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>(['network_error', 'timeout'])

/**
 * Sends one confirm attempt. `send` always carries this attempt's `Idempotency-Key` (the caller fixes it), so every
 * resend here is the same request (API.md §1.11). Every send gets only what is left of the attempt's one 30 s budget
 * as its timeout (`send(timeoutMs)`), so the patient never waits much more than 30 s in total:
 *   - `request_in_progress` → wait 1 s and send again, while the budget lasts;
 *   - a lost answer (`network_error` / `timeout`, no response) → exactly **one** replay, at once, with the time left
 *     (Frontend §4.10 (b): the replay shares the budget; decided 2026-10-09);
 *   - still unresolved after that (lost again, still in progress, or no budget left) → `booking_unconfirmed`, the
 *     P-11 outcome-unknown variant. Never a new key and never a blind retry beyond the one replay.
 * Every other answer is returned as it is.
 */
export async function confirmBooking<T>(
  send: (timeoutMs: number) => Promise<Outcome<T>>,
  { sleep = realSleep, now = () => performance.now(), budgetMs = BOOKING_TIMEOUT_MS }: ConfirmOptions = {},
): Promise<Outcome<T>> {
  const deadline = now() + budgetMs
  const unconfirmed: Outcome<T> = { ok: false, code: 'booking_unconfirmed' }
  let replayed = false
  for (;;) {
    const left = Math.floor(deadline - now())
    if (left <= 0) return unconfirmed
    const r = await send(left)
    if (r.ok) return r
    if (r.code === 'request_in_progress') {
      if (now() + IN_PROGRESS_WAIT_MS >= deadline) return unconfirmed
      await sleep(IN_PROGRESS_WAIT_MS)
      continue
    }
    if (LOST.has(r.code)) {
      if (replayed) return unconfirmed
      replayed = true
      continue
    }
    return r
  }
}
